import { Theme, useTheme } from "@/components/theme/themeProvider"
import { Button } from "@/components/ui/baseUi/button"
import { ChannelList } from "@/modules/chat/components/channelList"
import { useChatClient } from "@/modules/chat/stores/useChatStore"
import { useServicesContext } from "@/modules/core/services/services.context"
import { cn } from "@/modules/lib/utils"
import { useBoxNavigate } from "@/modules/navigation/hooks/useBoxNavigate"
import { Path } from "@/modules/navigation/routes"
import { LocalStorageKeys } from "@/modules/storage/localStorageKeys"
import { ChannelDetailsOverlay } from "@modules/chat/components/channelDetailsOverlay"
import { ChannelHeader } from "@modules/chat/components/channelHeader"
import { EmptyChannel } from "@modules/chat/components/emptyChannel"
import { NewChatChannelForm } from "@modules/chat/components/newChatChannelForm"
import { useChatPageContext } from "@modules/chat/contexts/chatPageContext"
import { useCreateChannel } from "@modules/chat/hooks/useCreateChat"
import { customReactions } from "@modules/chat/icons/reactions/reactions"
import { word } from "@modules/core/utils/i18n"
import { getFormatedDateTime } from "@modules/language/languageUtils"
import { ScreenHeader } from "@modules/ui/components/header"
import { ChevronLeftIcon } from "lucide-react"
import { useEffect, useEffectEvent, useState } from "react"
import { useLocation } from "react-router"
import type { ChannelFilters } from "stream-chat"
import {
  Channel,
  DateSeparator,
  MessageInput,
  MessageList,
  MessageTimestamp,
  Window,
  useChatContext,
} from "stream-chat-react"
import { EmojiPicker } from "stream-chat-react/emojis"

type ChatPageProps = {
  className?: string
} & React.ComponentProps<"div">

export function ChatPage({ className, ...props }: ChatPageProps) {
  const { setActiveChannel } = useChatContext()
  const chatClient = useChatClient()
  const { createOneToOneChannel } = useCreateChannel()
  const { localStorageService } = useServicesContext()
  const boxNavigate = useBoxNavigate()
  const { isCreatingNewChannel, isAddingMembersToChannel, displayChatDetailsOverlay } = useChatPageContext()
  const location = useLocation() as {
    state?: { userToChatTo?: string; programToChat?: string; returnTo?: { target: string; transactionId: string } }
  }
  const { theme } = useTheme()
  const [filters, setFilters] = useState<ChannelFilters>({
    members: {
      $in: [chatClient.user?.id ?? ""],
    },
    hidden: false,
  })
  const shouldShowComeBack = location.state?.returnTo?.target === "box-payin" && !!location.state.returnTo.transactionId
  const onComeBack = () => {
    localStorageService.save(LocalStorageKeys.boxTransaction.transactionId, {
      value: location.state!.returnTo!.transactionId,
    })
    boxNavigate(Path.Box.Finances.Root)
  }

  const fetchChannelsFromUser = useEffectEvent((userId: string) => {
    setFilters({
      members: {
        $in: [userId],
      },
      hidden: false,
    })

    void chatClient.queryChannels(filters).then((_) => {
      // timeout hack to set the active channel
      setTimeout(() => {
        if (location.state?.userToChatTo) {
          void createOneToOneChannel(location.state.userToChatTo)
        }
        const fetchSearchedChannel = async () => {
          if (location.state?.programToChat) {
            try {
              // Fetch the specific channel by ID
              const channels = await chatClient.queryChannels({
                id: { $eq: location.state.programToChat },
              })

              if (channels.length > 0) {
                const searchedChannel = channels[0]
                await searchedChannel?.watch() // Ensure the channel is initialized
                setActiveChannel(searchedChannel) // Set it as the active channel
              }
            } catch (error) {
              console.error("Error fetching the searched channel:", error)
            }
          }
        }

        void fetchSearchedChannel()
      }, 300)
    })
  })

  useEffect(() => {
    if (chatClient.user?.id) {
      fetchChannelsFromUser(chatClient.user.id)
    }
  }, [chatClient.user?.id])

  return (
    <div className={cn("flex w-full grow flex-col p-6", className)} {...props}>
      <ScreenHeader title={word("chat.header.title")} subTitle={word("chat.header.subTitle")} />
      {shouldShowComeBack && (
        <Button variant="underline" size="md" onClick={onComeBack} className="mb-4 w-fit">
          <ChevronLeftIcon className="size-4" />
          {word("chat.header.moveBackToTransaction")}
        </Button>
      )}
      <div className="chat flex">
        <ChannelList filters={filters} />
        {!isCreatingNewChannel && !isAddingMembersToChannel ? (
          Object.keys(chatClient.activeChannels).length > 0 ? (
            <Channel
              MessageTimestamp={() => (
                <MessageTimestamp format="HH:mm" customClass="str-chat__message-simple-timestamp" />
              )}
              LoadingIndicator={() => <></>}
              DateSeparator={({ date }) => (
                <DateSeparator date={date} formatDate={(date: Date) => getFormatedDateTime(date)} position="center" />
              )}
              reactionOptions={customReactions}
              EmojiPicker={() => <EmojiPicker pickerProps={{ theme: theme === Theme.OwnerDark ? "dark" : "light" }} />}
            >
              <Window>
                <ChannelHeader />
                <MessageList messageActions={["react", "quote", "delete"]} />
                <MessageInput additionalTextareaProps={{ className: "grow" }} />
              </Window>

              {displayChatDetailsOverlay && <ChannelDetailsOverlay />}
            </Channel>
          ) : (
            <EmptyChannel />
          )
        ) : (
          <NewChatChannelForm />
        )}
      </div>
    </div>
  )
}
